import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@mui/material';
import AppLoader from '../../../components/AppLoader/AppLoader';
import DescriptionIcon from '@mui/icons-material/Description';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import ElectricityConsumerBill from '../../../components/ElectricityConsumerBill/ElectricityConsumerBill';
import { fetchApprovedAdvanceBill } from '../../../components/ElectricityConsumerBill/fetchApprovedAdvanceBill';
import { mapApprovedAdvanceBillResponse } from '../../../components/ElectricityConsumerBill/mapApprovedAdvanceBillResponse';
import { getLogoColorLight } from '../../../components/ElectricityConsumerBill/discoBillBranding';
import './AdvancePaymentBillView.scss';

const BILL_NODE_ID = 'advance-payment-consumer-bill';

const AdvancePaymentBillView = () => {
    const { token } = useSelector((state) => state.auth);
    const [searchParams] = useSearchParams();
    const applicationId = searchParams.get('applicationId');
    const [billData, setBillData] = useState(null);
    const [loading, setLoading] = useState(Boolean(applicationId));
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState('');
    const [error, setError] = useState('');
    const isDownloadingRef = useRef(false);

    const getBillFileName = useCallback(() => {
        const trackingId = billData?.applicationId || applicationId;
        return trackingId ? String(trackingId).trim() : 'advance-payment-bill';
    }, [billData?.applicationId, applicationId]);

    const handleDownloadPdf = useCallback(async () => {
        const billNode = document.getElementById(BILL_NODE_ID);
        if (!billNode || isDownloadingRef.current) return;

        isDownloadingRef.current = true;
        setDownloading(true);
        setDownloadError('');

        try {
            const canvas = await html2canvas(billNode, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false,
                width: billNode.scrollWidth,
                height: billNode.scrollHeight,
                onclone: (clonedDoc) => {
                    const clonedBill = clonedDoc.getElementById(BILL_NODE_ID);
                    if (!clonedBill) return;

                    clonedBill.style.overflow = 'visible';
                    const logoColor =
                        clonedBill.style.getPropertyValue('--bill-logo-color') || '#1b6b38';
                    clonedBill.style.setProperty(
                        '--bill-logo-color-light',
                        getLogoColorLight(logoColor)
                    );
                },
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.98);
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true,
            });

            const pageWidth = 210;
            const pageHeight = 297;
            const renderedHeight = (canvas.height * pageWidth) / canvas.width;

            if (renderedHeight <= pageHeight) {
                pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, renderedHeight);
            } else {
                const renderedWidth = (canvas.width * pageHeight) / canvas.height;
                pdf.addImage(imgData, 'JPEG', 0, 0, renderedWidth, pageHeight);
            }

            pdf.save(`${getBillFileName()}.pdf`);
        } catch (downloadErr) {
            setDownloadError('Failed to download PDF. Please try Print Bill or refresh the page.');
        } finally {
            isDownloadingRef.current = false;
            setDownloading(false);
        }
    }, [getBillFileName]);

    const handlePrintBill = useCallback(() => {
        const billNode = document.getElementById(BILL_NODE_ID);
        if (!billNode) return;

        const printFrame = document.createElement('iframe');
        printFrame.setAttribute('aria-hidden', 'true');
        printFrame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
        document.body.appendChild(printFrame);

        const printDocument = printFrame.contentWindow?.document;
        if (!printDocument) {
            document.body.removeChild(printFrame);
            return;
        }

        const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
            .map((node) => node.outerHTML)
            .join('');
        const billFileName = getBillFileName();

        printDocument.open();
        printDocument.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>${billFileName}</title>
                    <style>
                        @page {
                            size: A4 portrait;
                            margin: 0;
                        }
                        html, body {
                            margin: 0 !important;
                            padding: 0 !important;
                            width: 210mm !important;
                            height: 297mm !important;
                            max-height: 297mm !important;
                            overflow: hidden !important;
                            background: #ffffff !important;
                        }
                        @media print {
                            *, *::before, *::after {
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                                color-adjust: exact !important;
                            }
                        }
                    </style>
                    ${styles}
                </head>
                <body>${billNode.outerHTML}</body>
            </html>
        `);
        printDocument.close();

        const cleanup = () => {
            if (printFrame.parentNode) {
                printFrame.parentNode.removeChild(printFrame);
            }
        };

        printFrame.contentWindow.onload = () => {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
            setTimeout(cleanup, 1000);
        };
    }, [getBillFileName]);

    useEffect(() => {
        if (!applicationId) {
            setError('Application ID is missing.');
            setLoading(false);
            return;
        }

        if (!token) {
            setError('Session expired. Please sign in again.');
            setLoading(false);
            return;
        }

        let isMounted = true;

        const loadBill = async () => {
            setLoading(true);
            setError('');

            try {
                const response = await fetchApprovedAdvanceBill(applicationId, token);
                const mappedBillData = mapApprovedAdvanceBillResponse(response);

                if (!isMounted) return;

                if (!mappedBillData) {
                    setBillData(null);
                    setError('No bill data found for this application.');
                    return;
                }

                setBillData(mappedBillData);
            } catch (fetchError) {
                if (!isMounted) return;
                setBillData(null);
                setError(fetchError.message || 'Failed to load bill data.');
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadBill();

        return () => {
            isMounted = false;
        };
    }, [applicationId, token]);

    useEffect(() => {
        if (!billData?.applicationId) return undefined;

        const previousTitle = document.title;
        document.title = billData.applicationId;

        return () => {
            document.title = previousTitle;
        };
    }, [billData?.applicationId]);

    return (
        <div className="ap-bill-view ap-bill-view--standalone">
            <div className="ap-bill-view__toolbar">
                <div className="ap-bill-view__status">Electricity Consumer Bill</div>
                <div className="ap-bill-view__actions">
                    {downloadError ? (
                        <div className="ap-bill-view__download-error">{downloadError}</div>
                    ) : null}
                    <Button
                        type="button"
                        variant="outlined"
                        className="ap-bill-view__print-btn"
                        onClick={handleDownloadPdf}
                        disabled={!billData || loading || downloading}
                    >
                        {downloading ? (
                            <AppLoader size={16} />
                        ) : (
                            <DescriptionIcon sx={{ color: '#006ad1', fontSize: '1rem' }} />
                        )}
                        Download PDF
                    </Button>
                    <Button
                        type="button"
                        variant="outlined"
                        className="ap-bill-view__print-btn"
                        onClick={handlePrintBill}
                        disabled={!billData || loading || downloading}
                    >
                        Print Bill
                    </Button>
                </div>
            </div>
            <div className="ap-bill-view__content">
                {loading ? (
                    <div className="ap-bill-view__loading">
                        <AppLoader size={36} />
                    </div>
                ) : error ? (
                    <div className="ap-bill-view__error">{error}</div>
                ) : (
                    <ElectricityConsumerBill
                        id={BILL_NODE_ID}
                        billData={billData}
                    />
                )}
            </div>
        </div>
    );
};

export default AdvancePaymentBillView;
